import moment from "moment";
import React, { FC, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { PlotEvent } from "@ant-design/charts";
import * as _ from "lodash";
import { useDeepCompareCallback, useDeepCompareEffect } from "use-deep-compare";
import ReactJson from "react-json-view";

import {
  Charts,
  ExtendedEventEntity,
  Filter,
  MetricEntity,
  V3DisplayFilter,
  Breadcrumbs,
  Enterprise,
  EventSourceType,
  ISODuration,
  DataDictionary,
  DataDescriptorEntity,
  ChartType,
  ChartTimePeriod,
  DisplayFilterCollection,
  DataFilterKVMap,
  DataFilterCollection
} from "@ctra/api";

import {
  Charts as ChartUI,
  CtraLayout,
  SwitchWithLabel,
  Row,
  Col,
  Typography,
  Button,
  PlusCircleOutlined,
  ArrowLeftOutlined
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { clampChartUnitList, flatten, isDebugMode, Nullable, parseQuery } from "@ctra/utils";
import { GAEvent } from "@ctra/analytics";

import { useFarm } from "@farms";
import { CompiledRoutes, URIState } from "@routes";
import { EventListContext } from "@events";
import { useDataDictionary } from "@base";
import { useSavedCharts, GACategories } from "@analytics";

import {
  Chart,
  ChartAPIContext,
  ChartFilterContext,
  EventList,
  ISODurationFilter,
  useTimePeriod
} from "@chart";

import { OverlayManager } from "../OverlayManager";
import styles from "./AddChartPage.module.less";

const { WidgetWrapper, ContentWrapper } = CtraLayout;
const { ChartVariant } = ChartUI;
const { Paragraph } = Typography;

const {
  components: { Breadcrumb }
} = Breadcrumbs;

const {
  analytics: {
    correlations: { pageDescription, events },
    v3: {
      config: { cta }
    },
    config: {
      metric: { displayName }
    }
  }
} = Content;

/**
 * Resolve the time unit from the data descriptor
 * @param {DataDescriptorEntity} dataDescriptorEntity
 * @returns {ISODuration}
 */
const resolveBucketSize = (dataDescriptorEntity: DataDescriptorEntity): ISODuration => {
  const isoDurationList = clampChartUnitList(DataDictionary.utils.resolveISODuration(dataDescriptorEntity));
  const defaultBucketSize = _.get(dataDescriptorEntity, ["valueProperties", "defaultBucketSize"]);

  const baseBucketSize = _.get(
    dataDescriptorEntity,
    ["valueProperties", "baseTimeGranularity"],
    defaultBucketSize
  );

  return _.includes(isoDurationList, baseBucketSize)
    ? baseBucketSize
    : _.defaultTo(_.first(isoDurationList), DataDictionary.DefaultDataInterval);
};

/**
 * Chart configurator page
 * @return {JSX.Element}
 * @constructor
 */
export const AddChartPage: FC = () => {
  const { t } = useTranslation();
  const { farm: farmContext, site: siteContext } = useFarm();

  const {
    metrics,
    dataDescriptors,
    meta: { isLoading },
    groups: { projectionMetrics },
    api: { getDefaultVariant, getChartInfo }
  } = useDataDictionary();

  const {
    savedCharts: allCharts,
    api: { saveChart },
    meta: { isUpdating }
  } = useSavedCharts();

  /**
   * Set up a ref for sharing and 1CF
   * @type {React.MutableRefObject<any>}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ref = useRef<any>();

  /**
   * Resolve what event types to show
   */
  const [showEvents, setShowEvents] = useState<Record<EventSourceType, boolean>>(
    _.mapValues({ ..._.invert(_.toPlainObject(EventSourceType)) }, () => false) as Record<
      EventSourceType,
      boolean
    >
  );

  /**
   * Manage the list of events
   */
  const [eventList, setEventList] = useState<ExtendedEventEntity[]>([]);

  /**
   * Get the group ID and the initial metric ID from the URL params
   */
  const { dashboard: groupID, metricID: initialMetricID } =
    useParams<{ dashboard: string; metricID: string }>();

  /**
   * Use the search query for the variant ID
   */
  const { search, state } = useLocation<URIState>();
  const searchParams = new URLSearchParams(search);
  const { variant: initialVariantID } = parseQuery(searchParams, "variant");

  /**
   * Get the group ID and name
   * @type {{id: string} | undefined}
   */
  const group = _.find(allCharts, { id: groupID });
  const groupName = _.get(group, "name");

  /**
   * Keep track of the charts
   * @note via URL, we only support setting up the first chart
   */
  const [chartCollection, setChartCollection] = useState<
    Array<{
      metricID: MetricEntity["id"];
      variantID: DataDescriptorEntity["id"];
    }>
  >([
    {
      metricID: initialMetricID,
      variantID: initialVariantID || getDefaultVariant(initialMetricID).id
    }
  ]);

  /**
   * Custom bits of chart config
   */
  const [clientParams, setClientParams] = useState<Record<string, string>>({});

  /**
   * Get the variant IDs from the chart collection
   * @type {string[]}
   */
  const variantIDCollection = _.map(chartCollection, "variantID");

  /**
   * Get the data descriptors for the chart collection
   * @type {DataDescriptorEntity[]}
   */
  const metricVariants = _.map(variantIDCollection, (variantID) => dataDescriptors[variantID]);

  /**
   * Get the metric IDs from the chart collection
   * @type {MetricEntity["id"][]}
   */
  const metricIDCollection = _.map(chartCollection, "metricID");

  /**
   * Get the metric entities
   * @type {MetricEntity[]}
   */
  const metricEntities = _.map(metricIDCollection, (metricID) => metrics[metricID]);

  /**
   * Main chart and overlays
   */
  const [main] = metricVariants;

  /**
   * One time pariod for all charts
   * @type {ISODuration}
   */
  const initialISODuration = DataDictionary.utils.resolveISODuration(main);
  const [isoDuration, setISOduration] = useState<Nullable<ISODuration>>(initialISODuration);
  const [initialTimePeriod] = useTimePeriod({ isoDuration }, _.defaultTo(0, 86400));
  const [timePeriod, setTimePeriod] = useState<ChartTimePeriod>(initialTimePeriod);

  /**
   * One bucket size for all (overlay manager must prevent selection of incompatible bucket sizes)
   * @type {ISODuration}
   */
  const initialBucketSize = resolveBucketSize(main);
  const [bucketSize, setBucketSize] = useState<ISODuration>(initialBucketSize);

  /**
   * Check if the event panel should be hidden
   * @type {boolean}
   */
  const eventPanelHidden = _.isEmpty(eventList);

  /**
   * Only set this flag when every chart supports it
   * @type {boolean}
   */
  const isMultiDataIntervalSupported = _.every(metricVariants, (dataDescriptorEntity) =>
    _.get(dataDescriptorEntity, ["flags", "isMultiDataIntervalSupported"], false)
  );

  /**
   * Value change handler for dropdowns associated with metric/source/variant changes
   * @param {Array<{metricID: MetricEntity["id"], variantID: DataDescriptorEntity["id"]}>} values
   */
  const handleChange = (
    values: Array<{ metricID: MetricEntity["id"]; variantID: DataDescriptorEntity["id"] }>
  ) => {
    setDebugInfo((info) => ({ ...info, metrics: values }));
    setChartCollection(values);
  };

  /**
   * Handle saving the chart collection to a card
   * @type {(view: ChartType, dataFilters: DataFilterKVMap, displayFilters?: DisplayFilterCollection, clientParams?: Record<string, string>) => void}
   */
  const handleSave = useDeepCompareCallback(
    (
      view: ChartType,
      dataFilters: DataFilterKVMap,
      displayFilters?: DisplayFilterCollection,
      clientParams?: Record<string, string>
    ) => {
      /**
       * Make an ISO duration or a date range tuple
       * @type {[string]}
       */
      const dateRange: [ISODuration] | [string, string] = isoDuration
        ? [isoDuration]
        : [timePeriod.startDate, timePeriod.endDate];

      /**
       * Convert filters into type + value format
       */
      const convertedDataFilters: DataFilterCollection = _.mapValues(dataFilters, (filters) => {
        return _.map(
          filters,
          (filter, key) =>
            ({
              type: key,
              values: filter
            } as Filter)
        );
      });

      if (groupID && groupName) {
        saveChart(_.map(chartCollection, "variantID"), groupID, groupName, {
          chartType: view,
          dateRange,
          bucketSize,
          dataFilters: convertedDataFilters,
          displayFilters,
          clientParams
        });

        Enterprise.history.push(
          CompiledRoutes.app.analytics.dashboard.index({
            dashboard: groupID
          })
        );
      } else {
        throw new Error("Group ID or Group Name is missing.");
      }
    },
    [chartCollection]
  );

  /**
   * If every metric in the collection is a projection metric, add a month to the end date
   */
  useDeepCompareEffect(() => {
    /**
     * Tell whether all the metrics allow projections
     * @type {boolean}
     */
    const isProjectionMetric = _.every(chartCollection, ({ metricID }) =>
      _.includes(projectionMetrics, metricID)
    );

    if (isProjectionMetric) {
      initialTimePeriod.endDate = moment(initialTimePeriod.endDate).add(1, "months").toISOString();
      setISOduration(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectionMetrics, chartCollection]);

  /**
   * Update the time period when the ISO duration changes
   */
  useDeepCompareEffect(() => {
    const bucketSizeList = clampChartUnitList(isoDuration || timePeriod);

    if (!_.includes(bucketSizeList, bucketSize)) {
      setBucketSize(_.first(bucketSizeList) as ISODuration);
    }
  }, [timePeriod, bucketSize, isoDuration]);

  /**
   * Reset the event list when the chart changes
   */
  useDeepCompareEffect(() => {
    setEventList([]);
  }, [chartCollection]);

  /**
   * Reset the event list when the show events change
   */
  useDeepCompareEffect(() => {
    if (eventList.length > 0 && !_.some(showEvents)) {
      setEventList([]);
    }
  }, [showEvents]);

  /**
   * Make debugging easier
   */
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown>>({
    metrics: [chartCollection],
    filePath:
      "packages/apps/enterprise/src/modules/analytics/components/chart-layout/v3/AddChartPage/AddChartPage.tsx"
  });

  /**
   * Get the metric ID and variant ID of the main chart
   */
  const [{ metricID: mainChartMetricID, variantID: mainChartVariantID }] = chartCollection;

  if (!mainChartMetricID) {
    throw new Error("Main chart metric ID is missing. Investigate how this can happen.");
  }

  if (!mainChartVariantID) {
    throw new Error(
      "Main chart variant ID is missing. Make sure the at least the default variant for the matric is used."
    );
  }

  return (
    <EventListContext.Provider farmID={farmContext?.id} siteID={siteContext?.id}>
      <ChartFilterContext.Provider
        isoDuration={isoDuration}
        timePeriod={timePeriod}
        bucketSize={bucketSize}
        currentValues={{ bucketSize, isoDuration, timePeriod }}
      >
        <ChartAPIContext.Provider metricVariantID={variantIDCollection} farmID={farmContext?.id}>
          {groupName && (
            <Breadcrumb
              path={CompiledRoutes.app.analytics.dashboard.index({ dashboard: groupID })}
              title={groupName}
            />
          )}
          <Breadcrumb
            title={t<string>(displayName(getChartInfo(mainChartMetricID, mainChartVariantID).nameToDisplay), {
              makeDefaultValue: true,
              collectionSize: _.size(chartCollection)
            })}
          />
          {isDebugMode() ? (
            <div onClick={(e) => e.stopPropagation()}>
              <ReactJson
                collapsed
                style={{
                  border: "1px dashed #ccc",
                  padding: 16,
                  margin: "0 0 32px",
                  maxHeight: 300,
                  overflow: "scroll"
                }}
                src={debugInfo}
              />
            </div>
          ) : null}
          <Button
            size="large"
            type="secondary"
            onClick={() =>
              Enterprise.history.push({
                pathname: CompiledRoutes.app.analytics.dashboard.addChart.index({
                  dashboard: groupID
                }),
                state
              })
            }
            icon={<ArrowLeftOutlined />}
          >
            {t<string>(cta.back)}
          </Button>

          <WidgetWrapper
            omitHeader
            className={styles.WidgetWrapper}
            loading={isLoading}
            title={<Paragraph>{t<string>(pageDescription)}</Paragraph>}
          >
            <Row gutter={[24, 0]} wrap={false}>
              <Col flex={1}>
                <Row className={styles.Options} justify="end" align="middle">
                  {farmContext && (
                    <EventListContext.Consumer>
                      {({ events: eventList }) =>
                        _.size(eventList) > 0 && (
                          <>
                            <Col>
                              <SwitchWithLabel
                                data-gtm-category={GACategories.configurator}
                                data-gtm-action="Toggle aiModel annotations"
                                // inverted the showEvents because of render delay
                                data-gtm-info={JSON.stringify({ visible: !showEvents })}
                                defaultChecked={showEvents[EventSourceType.aiModel]}
                                onChange={(checked) =>
                                  setShowEvents((current) => ({
                                    ...current,
                                    [EventSourceType.aiModel]: checked
                                  }))
                                }
                                size="small"
                                label={t<string>(events, {
                                  variant: "ai"
                                })}
                              />
                            </Col>
                            <Col className={styles.RightMargin}>
                              <SwitchWithLabel
                                data-gtm-category={GACategories.configurator}
                                data-gtm-action="Toggle events"
                                // inverted the showEvents because of render delay
                                data-gtm-info={JSON.stringify({ visible: !showEvents })}
                                defaultChecked={_.every(_.omit(showEvents, EventSourceType.aiModel))}
                                onChange={(checked) =>
                                  setShowEvents(({ AIModel, ...rest }) => ({
                                    AIModel,
                                    ..._.mapValues(rest, () => checked)
                                  }))
                                }
                                size="small"
                                label={t<string>(events, { variant: "events" })}
                              />
                            </Col>
                          </>
                        )
                      }
                    </EventListContext.Consumer>
                  )}
                  <Col>
                    <ChartAPIContext.Consumer>
                      {({
                        // the view will be determined by the main chart
                        charts: [
                          {
                            entity: { view }
                          }
                        ]
                      }) => (
                        <ISODurationFilter
                          data-gtm-category={GACategories.configurator}
                          data-gtm-action="Change ISO duration"
                          durations={Charts.utils.supportedDurations}
                          duration={isoDuration}
                          buckets={
                            isMultiDataIntervalSupported && Charts.utils.hasBucketSizeSupport(view)
                              ? void 0
                              : []
                          } // default time units, unless no support for multi intervals
                          bucketSize={bucketSize}
                          timePeriod={timePeriod}
                          onDurationChange={setISOduration}
                          onTimePeriodChange={setTimePeriod}
                          onUnitChange={setBucketSize}
                        />
                      )}
                    </ChartAPIContext.Consumer>
                  </Col>
                </Row>
                <ContentWrapper padded={false} className={styles.Wrapper}>
                  <ChartFilterContext.Consumer>
                    {({ api: { getSeries } }) => (
                      <ChartAPIContext.Consumer>
                        {({
                          metricVariants,
                          // the view is determined by the main chart
                          charts: [
                            {
                              entity: { view }
                            }
                          ]
                        }) => {
                          const variantIDCollection = _.map(metricVariants, "id");

                          /**
                           * Display filters for the chart
                           * @type {Record<string, Array<V3DisplayFilter>>}
                           */
                          const displayFilters = _.reduce(
                            variantIDCollection,
                            (acc, variantID) => {
                              const series = getSeries(variantID);

                              if (_.isEmpty(series.active)) {
                                return acc;
                              }

                              acc[variantID] = [
                                {
                                  type: "series",
                                  values: series.active
                                }
                              ];

                              return acc;
                            },
                            {} as Record<DataDescriptorEntity["id"], Array<V3DisplayFilter>>
                          );

                          /**
                           * Metric name for GA
                           */
                          const metricName = t<string>(displayName(metricEntities[0].name), {
                            makeDefaultValue: true
                          });

                          /**
                           * Enabled views for the overlay manager
                           * @type {ChartType[]}
                           */
                          const enabledViews = _.map(main.supportedCharts, (chartID) => {
                            const [, view] = _.split(chartID, "-");

                            return view as ChartType;
                          });

                          return (
                            <>
                              <OverlayManager
                                enabledViews={enabledViews}
                                currentView={view}
                                initialValues={{ items: chartCollection }}
                                onSubmit={() => void 0}
                                onChange={({ items }) => {
                                  handleChange(items);
                                }}
                              />
                              <Row wrap={false}>
                                <Col span={24} className={styles.Chart}>
                                  <Chart
                                    ref={ref}
                                    variant={ChartVariant.V3}
                                    viewOptions={{
                                      showEvents,
                                      zoomEnabled: false,
                                      showSupportedViews: true,
                                      showToolbar: false,
                                      showAnnotations: true
                                    }}
                                    config={{
                                      onReady: (plot) => {
                                        ref.current = plot;

                                        plot.on("afterrender", () => {
                                          const config = _.get(ref, ["current", "contextValues", "config"]);

                                          setClientParams(_.pickBy(flatten(config), _.isString));
                                        });

                                        plot.on("element:click", (event: PlotEvent) => {
                                          const shape = _.get(event, ["data", "shape"]);
                                          const eventList = _.get(event, ["data", "data", "events"]);

                                          GAEvent(
                                            GACategories.configurator,
                                            "Open event",
                                            JSON.stringify({ eventList })
                                          );

                                          if (shape === "farm-event-point") {
                                            setEventList(() => eventList);
                                          }
                                        });
                                      }
                                    }}
                                  />
                                </Col>
                              </Row>
                              <Row justify="end" className={styles.Buttons}>
                                <Button
                                  loading={isUpdating}
                                  className={styles.RightMargin}
                                  icon={<PlusCircleOutlined />}
                                  size="large"
                                  type="primary"
                                  data-gtm-category={GACategories.dashboards}
                                  data-gtm-action="Save metric to dashboard"
                                  data-gtm-info={JSON.stringify({ metric: metricName })}
                                  onClick={() => {
                                    const variantIDCollection = _.map(chartCollection, "variantID");

                                    const filters = _.reduce(
                                      variantIDCollection,
                                      (acc, variantID) => {
                                        acc[variantID] = _.get(filters, [variantID], {});

                                        return acc;
                                      },
                                      {} as DataFilterKVMap
                                    );

                                    handleSave(view, filters, displayFilters, clientParams);
                                  }}
                                >
                                  {t<string>(cta.save, { isSaved: false })}
                                </Button>
                                <Button
                                  size="large"
                                  type="secondary"
                                  data-gtm-category={GACategories.dashboards}
                                  data-gtm-action="Cancel saving"
                                  onClick={() =>
                                    Enterprise.history.push(
                                      CompiledRoutes.app.analytics.dashboard.addChart.index({
                                        dashboard: groupID
                                      })
                                    )
                                  }
                                >
                                  {t<string>(cta.cancel)}
                                </Button>
                              </Row>
                            </>
                          );
                        }}
                      </ChartAPIContext.Consumer>
                    )}
                  </ChartFilterContext.Consumer>
                </ContentWrapper>
              </Col>
              {eventPanelHidden ? null : (
                <Col span={5}>
                  <EventList
                    variant={
                      _.every(eventList, (event) => event.source.type === EventSourceType.aiModel)
                        ? EventList.variant.insights
                        : EventList.variant.events
                    }
                    data={eventList}
                    handleClose={() => setEventList([])}
                  />
                </Col>
              )}
            </Row>
          </WidgetWrapper>
        </ChartAPIContext.Provider>
      </ChartFilterContext.Provider>
    </EventListContext.Provider>
  );
};
